import type { ValueField, GroupField } from '@certhon/models';
import { resolveLocalized, usePreviewLocale } from './PreviewLocaleContext';

type TitledField = Pick<ValueField | GroupField, 'title_en' | 'title_nl'>;

type DescribedField = TitledField &
  Partial<Pick<ValueField, 'description_en' | 'description_nl'>>;

interface FieldTitleProps {
  field: DescribedField;
  /** When true, render the description below the title. */
  showDescription?: boolean;
}

/**
 * Localized title/description display.
 * Uses the PreviewLocale context to determine which language to show first,
 * falling back to the other language, then empty string.
 */
export function FieldTitle({ field, showDescription }: FieldTitleProps) {
  const locale = usePreviewLocale();
  const title = resolveLocalized(field.title_nl, field.title_en, locale);
  const description =
    showDescription && 'description_nl' in field
      ? resolveLocalized(
          (field as DescribedField).description_nl,
          (field as DescribedField).description_en,
          locale,
        )
      : '';

  return (
    <div className="min-w-0 flex-1">
      <span className="text-sm leading-tight">{title}</span>
      {description && (
        <p className="text-muted-foreground mt-0.5 text-xs leading-snug">
          {description}
        </p>
      )}
    </div>
  );
}
